'use client'

import { Suspense, useState } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { loginT } from '@/src/translations/login'

function LoginForm() {
  const params = useParams<{ locale: string }>()
  const locale = params.locale || 'en'
  const t = loginT(locale)
  const returnTo = useSearchParams().get('returnTo') || `/${locale}/account`

  const [channel, setChannel] = useState<'email' | 'sms'>('email')
  const [identifier, setIdentifier] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'id' | 'code'>('id')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function send() {
    if (!identifier.trim()) return
    setLoading(true)
    setError('')
    try {
      const r = await fetch('/api/auth/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, identifier: identifier.trim() }),
      })
      if (r.ok) setStep('code')
      else setError(t.errorSend)
    } finally {
      setLoading(false)
    }
  }

  async function verify() {
    if (!code.trim()) return
    setLoading(true)
    setError('')
    try {
      const r = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, identifier: identifier.trim(), code: code.trim() }),
      })
      const data = await r.json().catch(() => ({}))
      if (r.ok && data.authenticated) {
        window.location.href = returnTo
      } else if (data.status === 'signup_required') {
        setError(t.errorSignupRequired)
      } else {
        setError(t.errorInvalidCode)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-6 py-16">
      <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-neutral-900">{t.title}</h1>
        <p className="mt-1 text-sm text-neutral-500">
          {step === 'id' ? t.intro : t.codeSent(channel)}
        </p>

        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {step === 'id' ? (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-1 rounded-full bg-neutral-100 p-1 text-sm">
              {(['email', 'sms'] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setChannel(c)}
                  className={`rounded-full py-1.5 font-medium transition ${channel === c ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500'}`}
                >
                  {c === 'email' ? t.channelEmail : t.channelPhone}
                </button>
              ))}
            </div>
            <input
              type={channel === 'email' ? 'email' : 'tel'}
              autoFocus
              inputMode={channel === 'email' ? 'email' : 'tel'}
              placeholder={channel === 'email' ? t.emailPlaceholder : '+49 170 1234567'}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              className="w-full rounded-2xl border border-neutral-200 px-4 py-3 text-sm outline-none focus:border-neutral-900"
            />
            <button
              onClick={send}
              disabled={loading || !identifier.trim()}
              className="w-full rounded-full bg-neutral-900 py-3 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
            >
              {loading ? t.sending : t.sendCode}
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <input
              type="text"
              autoFocus
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
              className="w-full rounded-2xl border border-neutral-200 px-4 py-3 text-center text-lg tracking-[0.3em] outline-none focus:border-neutral-900"
            />
            <button
              onClick={verify}
              disabled={loading || !code.trim()}
              className="w-full rounded-full bg-neutral-900 py-3 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
            >
              {loading ? t.verifying : t.verify}
            </button>
            <button onClick={() => { setStep('id'); setCode(''); setError('') }} className="w-full text-center text-xs text-neutral-400 underline">
              {t.changeIdentifier}
            </button>
          </div>
        )}
      </div>
    </main>
  )
}

// useSearchParams() must sit inside a Suspense boundary for the production
// prerender (Next CSR bailout); the login form is otherwise fully client-side.
export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  )
}
